import React, { useRef, useEffect, useCallback } from 'react';
import { Database, BookOpen, Wrench, GitBranch, Check, ExternalLink } from 'lucide-react';

const PlatformPillarsSection = () => {
  const videoRefs = useRef([]);
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // 50% visibility threshold
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Play video when 50%+ is visible
          video.play().catch(err => console.log('Video play error:', err));
        } else {
          // Pause video when less than 50% is visible
          video.pause();
        }
      });
    };

    observerRef.current = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all existing video elements
    videoRefs.current.forEach((video) => {
      if (video) observerRef.current.observe(video);
    });

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addVideoRef = useCallback((el) => {
    if (el && !videoRefs.current.includes(el)) {
      videoRefs.current.push(el);
      // Observe the video element when it's added
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    }
  }, []);
  const pillars = [
    {
      id: 0,
      icon: Database,
      title: "OnePlace NLQ Engine",
      subtitle: "A semantic reasoning engine designed to understand your structured data, context, and intent.",
      accuracy: "Delivers **95%+** accuracy on leading Text-to-SQL benchmarks.",
      features: [
        {
          title: "AI-Assisted Data Onboarding",
          items: [
            "Connect any database — PostgreSQL, BigQuery, MySQL, MSSQL, Oracle, and other SQL dialects",
            "Smart schema detection with context-aware relationship mapping",
            "AI-assisted, one-click table onboarding",
            "Automatic schema and column refresh from live databases"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/g7q68lwj_ds_onboarding_web_1080.mp4"
        },
        {
          title: "Semantic Enrichment",
          items: [
            "Add business context with KPIs, metrics, and target questions",
            "Auto-generate SQL queries from data models and cache for faster response times",
            "Build Domains — curated, queryable layers across data sources",
            "Keep your semantic layer continuously aligned with live data"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/55xmrolg_domain_onboarding_web_1080.mp4"
        },
        {
          title: "Conversational BI Agent",
          items: [
            "Query data naturally across multiple SQL dialects",
            "High-precision SQL generation with business-aware context",
            "Built-in memory and conversation tracing",
            "Full observability and traceability for every generated query"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/8meq1dcl_biagent_1080.mp4"
        }
      ],
      // No static images - using videos in features instead
      conclusion: "From schema to semantics to SQL — the entire analytics lifecycle, reimagined."
    },
    // Knowledge Bases section removed as requested
    {
      id: 1,
      icon: Wrench,
      title: "OnePlace Tool Management", 
      subtitle: "One unified suite to onboard, manage, and deploy tools across your organization.",
      features: [
        {
          title: "Create & Manage All Tools",
          items: [
            "Build Database Tools, NLQ Tools, RAG Tools, and API Tools from a single console",
            "Connect internal systems or external providers",
            "Multiple authentication modes supported",
            "Associate domains or data sources with NLQ tools for downstream agent use",
            "Create tools from knowledge bases — instantly usable by agents"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/xxem69gl_tool_1080.mp4"
        },
        {
          title: "MCP Server Management & External Integrations",
          items: [
            "Create MCP servers directly from configured tools",
            "One-click deploy, monitor logs, and share URLs for downstream applications",
            "Connect external MCP servers seamlessly into your environment",
            "Full logging and monitoring for every deployed instance",
            "Ready-to-use MCP integrations for apps like Gmail, Google Drive, Docs, Slack etc."
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/zkgsqjgd_mcp_server_1080.mp4"
        }
      ],
      // No static images - using videos in features instead
      conclusion: "A single command center for your entire tool ecosystem — from data connectors to agent endpoints."
    },
    {
      id: 2,
      icon: GitBranch,
      title: "OnePlace Agent Builder & Observability Platform",
      subtitle: "Where everything comes together — data, knowledge, and tools converge into intelligent, explainable automation.",
      features: [
        {
          title: "AI-Assisted Workflow Builder",
          items: [
            "Intuitive, visual interface to design, edit, and deploy agents effortlessly",
            "Start from plain text — the AI assistant converts descriptions into executable workflows using available tools and integrations",
            "Iteratively refine workflows with notes, edits, and real-time testing",
            "One-click deploy and share agents across the organization"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/cf4b6uun_agent_720.mp4"
        },
        {
          title: "Deterministic & Transparent Execution",
          items: [
            "Execute workflows with deterministic, repeatable outcomes and full visibility",
            "Real-time traceability across inputs, outputs, tool calls, and reasoning steps",
            "Comprehensive logs and observability for every decision and action",
            "Agents act as reusable intelligence — built once, leveraged across projects"
          ],
          video: "https://customer-assets.emergentagent.com/job_knowledge-hub-161/artifacts/h7cp4ux9_observability_720.mp4"
        }
      ],
      // No static images - using videos in features instead
      conclusion: "Intelligent automation that explains every decision and learns from every execution."
    }
  ];

  const getImageGridClass = (totalCount) => {
    if (totalCount === 1) return 'image-grid single';
    if (totalCount === 2) return 'image-grid double';
    if (totalCount === 3) return 'image-grid triple';
    return 'image-grid';
  };

  return (
    <section id="platform-solutions" className="section-spacing bg-[var(--bg-primary)]">
      <div className="light-full-container">
        <div className="light-content-container">
          <div className="space-y-20">
            {/* Section Header */}
            <div className="text-center space-y-6">
              <h2 className="display-large">Platform Solutions</h2>
            </div>

            {/* Pillars */}
            {pillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              const isReverse = index % 2 === 1;
              
              return (
                <div key={pillar.id} className="space-y-12" id={pillar.id === 0 ? 'text-to-sql' : pillar.id === 1 ? 'tool-management' : 'agent-workflows'}>
                  {/* Pillar Header */}
                  <div className="text-center space-y-6">
                    <div className="flex items-center justify-center gap-4">
                      <div className="icon-container">
                        <IconComponent className="w-8 h-8" />
                      </div>
                      <h3 className="heading-1">
                        {pillar.title}
                      </h3>
                    </div>
                    <div className="space-y-4 max-w-4xl mx-auto">
                      <p className="body-large">
                        {pillar.subtitle}
                      </p>
                      {pillar.accuracy && (
                        <p className="text-2xl font-semibold text-[var(--text-primary)]">
                          Delivers <span className="text-[var(--brand-primary)]">95%+ accuracy</span> on leading Text-to-SQL benchmarks.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Content Layout - Special handling for video-based sections */}
                  {(pillar.id === 0 || pillar.id === 1 || pillar.id === 2) && pillar.features[0].video ? (
                    // Video-based sections with alternating layout
                    <div className="space-y-20">
                      {pillar.features.map((feature, featureIndex) => {
                        // For NLQ (id=0): start with video left (0=left, 1=right, 2=left)
                        // For Tool Management (id=1): start with video right (0=right, 1=left) since NLQ ended with video left  
                        // For Agent Builder (id=2): start with video right (0=right, 1=left) since Tool Management ended with video left
                        const isVideoLeft = pillar.id === 0 ? (featureIndex % 2 === 0) : (featureIndex % 2 === 1);
                        return (
                          <div key={featureIndex} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${isVideoLeft ? '' : 'lg:grid-flow-row-dense'}`}>
                            {/* Video */}
                            <div className={`${isVideoLeft ? '' : 'lg:col-start-2'}`}>
                              <div className="platform-image-container max-w-2xl mx-auto">
                                <video 
                                  ref={addVideoRef}
                                  src={feature.video}
                                  className="platform-image"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                                <div className="platform-image-overlay">
                                  <ExternalLink className="w-6 h-6 text-[var(--brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </div>
                            </div>

                            {/* Feature Content */}
                            <div className={`space-y-6 ${isVideoLeft ? '' : 'lg:col-start-1'}`}>
                              <h4 className="heading-2 text-[var(--brand-primary)]">{feature.title}</h4>
                              <ul className="space-y-4">
                                {feature.items.map((item, itemIndex) => (
                                  <li key={itemIndex} className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-[var(--brand-primary)] mt-0.5 flex-shrink-0" />
                                    <span className="body-medium">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        );
                      })}
                      
                      <div className="pt-8 text-center">
                        <p className="text-lg text-[var(--text-secondary)] font-normal italic mb-8">
                          {pillar.conclusion}
                        </p>
                        <div className="border-t border-[var(--border-subtle)]"></div>
                      </div>
                    </div>
                  ) : (
                    // Other pillars with standard layout
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${isReverse ? 'lg:grid-flow-row-dense' : ''}`}>
                      {/* Features */}
                      <div className={`space-y-12 ${isReverse ? 'lg:col-start-2' : ''}`}>
                        {pillar.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="space-y-6">
                            <h4 className="heading-3 text-[var(--brand-primary)]">{feature.title}</h4>
                            <ul className="space-y-4">
                              {feature.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start gap-3">
                                  <Check className="w-5 h-5 text-[var(--brand-primary)] mt-0.5 flex-shrink-0" />
                                  <span className="body-small">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        
                        <div className="pt-8">
                          <p className="text-lg text-[var(--text-secondary)] font-normal italic mb-8">
                            {pillar.conclusion}
                          </p>
                          <div className="border-t border-[var(--border-subtle)]"></div>
                        </div>
                      </div>

                      {/* Images and Video */}
                      <div className={`${isReverse ? 'lg:col-start-1' : ''}`}>
                        <div className={getImageGridClass((pillar.images?.length || 0) + (pillar.video ? 1 : 0))}>
                          {pillar.images?.map((imageUrl, imageIndex) => (
                            <div 
                              key={imageIndex}
                              className="platform-image-container"
                              style={{
                                animationDelay: `${imageIndex * 0.2}s`
                              }}
                            >
                              <img 
                                src={imageUrl}
                                alt={`${pillar.title} Screenshot ${imageIndex + 1}`}
                                className="platform-image"
                                loading="lazy"
                              />
                              <div className="platform-image-overlay">
                                <ExternalLink className="w-6 h-6 text-[var(--brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ))}
                          
                          {pillar.video && (
                            <div 
                              className="platform-image-container"
                              style={{
                                animationDelay: `${(pillar.images?.length || 0) * 0.2}s`
                              }}
                            >
                              <video 
                                src={pillar.video}
                                className="platform-image"
                                autoPlay
                                loop
                                muted
                                playsInline
                              />
                              <div className="platform-image-overlay">
                                <ExternalLink className="w-6 h-6 text-[var(--brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformPillarsSection;